import { ProblemError, type ProblemBody } from './problem';

export interface RequestOptions {
  /** Query string params. `undefined` / `null` values are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Extra headers — `Idempotency-Key` on order submit is the only routine use. */
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface ApiClientOptions {
  /** Defaults to `/api/v1`, matching the OpenAPI `servers` entry. */
  baseUrl?: string;
  /**
   * Called on every 401 before the ProblemError is thrown. The `@most/auth` package installs a
   * handler here that clears the TanStack Query cache and redirects to `/login?next=…`.
   */
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
}

const DEFAULT_BASE_URL = '/api/v1';

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private onUnauthorized?: () => void;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? DEFAULT_BASE_URL).replace(/\/+$/, '');
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.onUnauthorized = options.onUnauthorized;
  }

  /** Installed late by `@most/auth`, which needs the router and the query client first. */
  setUnauthorizedHandler(handler: () => void): void {
    this.onUnauthorized = handler;
  }

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, undefined, options);
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, body, options);
  }

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, body, options);
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, body, options);
  }

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, undefined, options);
  }

  /** multipart/form-data — certificates and product media. The browser sets the boundary. */
  upload<T>(path: string, form: FormData, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, form, options);
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    const url = this.baseUrl + path + buildQuery(options.query);

    const headers: Record<string, string> = {
      Accept: 'application/json, application/problem+json',
      ...options.headers,
    };

    let payload: BodyInit | undefined;
    if (body instanceof FormData) {
      payload = body;
      // Deliberately no Content-Type: fetch must set it, with the multipart boundary.
    } else if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }

    const response = await this.fetchImpl(url, {
      method,
      headers,
      body: payload,
      // The session cookie is HttpOnly; without this, every request is anonymous.
      credentials: 'include',
      signal: options.signal,
    });

    if (!response.ok) {
      const problem = await parseProblem(response);
      if (response.status === 401) this.onUnauthorized?.();
      throw new ProblemError(response.status, problem);
    }

    if (response.status === 204) return undefined as T;

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('json')) return undefined as T;

    return (await response.json()) as T;
  }
}

async function parseProblem(response: Response): Promise<ProblemBody> {
  try {
    const body = (await response.json()) as ProblemBody;
    if (body && typeof body === 'object') return body;
  } catch {
    // A proxy timeout or a 502 from Caddy is not problem+json. Fall through.
  }
  return { status: response.status, title: response.statusText };
}

function buildQuery(query: RequestOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** The process-wide client. All three SPAs share this instance. */
export const api = new ApiClient();
